/**
 * Pure, tiptap-free rendering helpers for the `callout` node.
 *
 * The icon is baked into the node's STATIC render (renderHTML / toDOM) as an
 * `<img>` whose `src` is a data-URI SVG — NOT drawn by a React NodeView. This
 * matters because the reader (components/sceneEditor/sceneChapterReader.js)
 * renders scenes through ProseMirror's DOMSerializer, which:
 *   - does not run in a context that guarantees React NodeViews reach the DOM
 *     for this read-only path (the earlier NodeView-only icon was invisible in
 *     the reader — box + text showed, the glyph did not), and
 *   - creates elements with document.createElement (HTML namespace), so a raw
 *     inline <svg> in a DOMOutputSpec would not render as a vector.
 * An <img> is a plain HTML element that renders the SVG in the live editor DOM,
 * in @tiptap/html `generateHTML` strings, and inside the BRNative WebView.
 *
 * Imports nothing from tiptap/@tiptap/react, so it is unit-testable in jest
 * (which does not transform those ESM packages) — this is how we prove the icon
 * SVG is present in the static output, not merely in a NodeView.
 */
import {resolveCalloutIcon} from './calloutIcons';

// Per-type glyph colour, baked into the data-URI SVG (an <img> data-URI cannot
// inherit CSS `currentColor`). Mirrors the accent colours in sceneEditor.css.
const TYPE_COLOR = {
  aside: '#607d8b',
  blurb: '#6a1b9a',
  note: '#1565c0',
  warning: '#c1701f',
  tip: '#2e7d32'
};

/**
 * Build a `data:image/svg+xml` URI for a callout's icon. The SVG is generated
 * by us from the bundled FontAwesome path data + a type-whitelisted colour —
 * never from author input — so it is safe to embed regardless of `html:false`.
 */
export function buildIconDataUri(iconName, type) {
  const icon = resolveCalloutIcon(iconName, type);
  const [width, height, , , pathData] = icon.icon;
  const d = Array.isArray(pathData) ? pathData.join(' ') : pathData;
  const color = TYPE_COLOR[type] || TYPE_COLOR.aside;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">` +
    `<path fill="${color}" d="${d}"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * ProseMirror DOMOutputSpec for the callout node's non-content chrome + content
 * hole. Returned as the tail of the node's renderHTML: the `<img>` glyph, then
 * the `<div class="callout__body">` whose `0` is the content hole.
 */
export function calloutInnerSpec(type, iconName) {
  return [
    [
      'img',
      {
        class: 'callout__glyph',
        src: buildIconDataUri(iconName, type),
        alt: '',
        'aria-hidden': 'true',
        contenteditable: 'false'
      }
    ],
    ['div', {class: 'callout__body'}, 0]
  ];
}
